// Battle effects, drawn in pixels on the battle screen: spells (fire, ice, thunder, light, healing...),
// weapon skills, creature moves, Magic Bursts, and the little marks that show someone's statuses.
// Everything is a function of time, so the same moment always looks the same.
import type { StatusId } from '../../../shared/jobs.js';

export type SpellFx =
  | 'slash' | 'sweep' | 'shout' | 'shield' | 'fire' | 'ice' | 'thunder' | 'sleep' | 'heal' | 'protect' | 'regen' | 'light' | 'raise'
  | 'steal' | 'poison' | 'haste' | 'smoke'
  | 'spore' | 'kick' | 'bash' | 'claw' | 'drain' | 'roar'
  | 'burst' | 'revive';

/** How long each effect plays (ms). */
export const FX_MS: Record<SpellFx, number> = {
  slash: 320, sweep: 380, shout: 520, shield: 600, fire: 720, ice: 700, thunder: 600, sleep: 800, heal: 760, protect: 640, regen: 700,
  light: 760, raise: 1000, steal: 480, poison: 640, haste: 520, smoke: 760, spore: 640, kick: 320, bash: 380, claw: 340, drain: 700, roar: 600,
  burst: 900, revive: 1000,
};

type C = CanvasRenderingContext2D;
const px = (c: C, x: number, y: number, w: number, h: number, col: string) => { c.fillStyle = col; c.fillRect(Math.round(x), Math.round(y), w, h); };
/** A steady pseudo-random number for particle i of effect seed s. */
const r = (s: number, i: number) => { const v = Math.sin(s * 12.9898 + i * 78.233) * 43758.5453; return v - Math.floor(v); };

/** Draw an effect at a target: (x, y) is their feet, `top` how tall they are, k = 0..1 through the effect. */
export function drawFx(c: C, fx: SpellFx, x0: number, y0: number, top0: number, k: number, seed: number): void {
  if (k < 0 || k > 1) return;
  // big creatures (drawn at double size) get double-size effects: whole pixels, so it stays crisp
  const u = top0 > 40 ? 2 : 1;
  c.save(); c.translate(Math.round(x0), Math.round(y0)); c.scale(u, u);
  try { drawAt(c, fx, 0, 0, top0 / u, k, seed, -Math.round(y0) / u); } finally { c.restore(); }
}

/** `sky` is the top of the battle screen in these coordinates (bolts and pillars come down from it). */
function drawAt(c: C, fx: SpellFx, x: number, y: number, top: number, k: number, seed: number, sky: number): void {
  const mid = y - top / 2, head = y - top, fade = Math.min(1, (1 - k) * 4);
  c.save(); c.globalAlpha *= fade;
  switch (fx) {
    case 'slash': case 'claw': {
      const n = fx === 'claw' ? 3 : 1, col = fx === 'claw' ? '#ff9a8a' : '#ffffff';
      for (let j = 0; j < n; j++) for (let i = 0; i < 14; i++) {
        const t = i / 13; if (t > k * 1.6) break;
        px(c, x - 9 + t * 18 + (j - (n - 1) / 2) * 4, head + 2 + t * (top - 4), 2, 1, i > 11 ? '#ffe9a8' : col);
      }
      break;
    }
    case 'sweep': {
      for (let i = 0; i < 24; i++) { const a = Math.PI * (0.15 + 0.7 * (i / 23)); if (i / 23 > k * 1.5) break; px(c, x - Math.cos(a) * 16, mid + Math.sin(a) * 6 - 2, 2, 1, i % 3 ? '#ffffff' : '#ffe9a8'); }
      break;
    }
    case 'shout': case 'roar': {
      const col = fx === 'shout' ? '#ec5448' : '#c08aff';
      for (let ring = 0; ring < 3; ring++) { const rr = (k * 3 - ring * 0.4) * 12; if (rr <= 0) continue; ring3(c, x, mid, rr, rr * 0.55, col, 20); }
      break;
    }
    case 'shield': case 'protect': {
      const col = fx === 'shield' ? '#a3def4' : '#8fc7ff', grow = Math.min(1, k * 3);
      for (let i = 0; i < 28; i++) { const a = (i / 28) * Math.PI * 2 + k * 2; if ((i + Math.floor(k * 20)) % 4 === 0) continue; px(c, x + Math.cos(a) * 12 * grow, mid + Math.sin(a) * 15 * grow, 1, 2, col); }
      if (k > 0.3 && k < 0.7) { c.globalAlpha *= 0.25; px(c, x - 10, head, 20, top, col); }
      break;
    }
    case 'fire': {
      for (let i = 0; i < 26; i++) {
        const life = (k * 1.4 - r(seed, i) * 0.4); if (life < 0 || life > 1) continue;
        const fx_ = x + (r(seed, i + 50) - 0.5) * 18 + Math.sin(life * 9 + i) * 2, fy = y - 2 - life * (top + 8) * (0.5 + r(seed, i + 9) * 0.5);
        const col = life < 0.3 ? '#fff3b0' : life < 0.6 ? '#f9b24a' : life < 0.85 ? '#ec5f3a' : '#7a3a3a', s = life < 0.6 ? 3 : 2;
        px(c, fx_, fy, s, s, col);
      }
      if (k < 0.35) { c.globalAlpha *= (0.35 - k) * 2; px(c, x - 12, y - 6, 24, 6, '#ffd27a'); }
      break;
    }
    case 'ice': {
      for (let i = 0; i < 9; i++) {
        const a = (i / 9) * Math.PI * 2, d = Math.max(0, 1 - k * 1.8) * 26, sx = x + Math.cos(a) * d, sy = mid + Math.sin(a) * d * 0.6;
        for (let j = 0; j < 4; j++) px(c, sx - Math.cos(a) * j * 1.5, sy - Math.sin(a) * j * 1.5, 1, 1, j ? '#bfe9ff' : '#ffffff');
      }
      if (k > 0.5) {   // the crystal
        const g = Math.min(1, (k - 0.5) * 4);
        for (let i = 0; i < 6; i++) { const a = i / 6 * Math.PI * 2; for (let j = 0; j < 8 * g; j++) px(c, x + Math.cos(a) * j, mid + Math.sin(a) * j, 1, 1, j % 2 ? '#a3def4' : '#ffffff'); }
      }
      break;
    }
    case 'thunder': {
      if (k < 0.55) {
        let bx = x + (r(seed, 1) - 0.5) * 10, by = sky;
        const end = mid, steps = 8, reach = Math.min(1, k / 0.25);
        for (let i = 0; i < steps; i++) {
          const ny = sky + (i + 1) / steps * (end - sky) * reach, nx = x + (r(seed, i + 3) - 0.5) * 12 * (1 - i / steps);
          line(c, bx, by, nx, ny, '#fffbe0'); line(c, bx + 1, by, nx + 1, ny, '#ffe97a');
          bx = nx; by = ny; if (ny >= sky + (end - sky) * reach - 0.5) break;
        }
      }
      if (k > 0.2 && k < 0.7) for (let i = 0; i < 10; i++) { const a = r(seed, i) * Math.PI * 2, d = (k - 0.2) * 40 * r(seed, i + 20); px(c, x + Math.cos(a) * d, mid + Math.sin(a) * d * 0.7, 2, 1, '#ffe97a'); }
      break;
    }
    case 'light': case 'raise': case 'revive': {
      const w = Math.sin(Math.min(1, k * 1.3) * Math.PI) * (fx === 'light' ? 9 : 7), col = fx === 'light' ? '#fffbe0' : '#ffe9a8';
      c.globalAlpha *= 0.75; px(c, x - w, sky, w * 2, y - sky, col);
      c.globalAlpha /= 0.75; px(c, x - w / 3, sky, Math.max(1, w * 2 / 3), y - sky, '#ffffff');
      for (let i = 0; i < 12; i++) { const life = (k * 1.5 - r(seed, i) * 0.5); if (life < 0 || life > 1) continue; px(c, x + (r(seed, i + 7) - 0.5) * 20, fx === 'light' ? y - life * top * 1.4 : head - 10 + life * (top + 10), 1, fx === 'light' ? 1 : 2, fx === 'light' ? '#ffffff' : '#fff6d8'); }
      break;
    }
    case 'heal': case 'regen': {
      for (let i = 0; i < 16; i++) {
        const life = (k * 1.4 - r(seed, i) * 0.4); if (life < 0 || life > 1) continue;
        const hx = x + (r(seed, i + 30) - 0.5) * 20, hy = y - life * (top + 6);
        const col = i % 3 === 0 ? '#ffffff' : fx === 'heal' ? '#8fe08a' : '#b7f0a0';
        if (i % 4 === 0) { px(c, hx - 1, hy, 3, 1, col); px(c, hx, hy - 1, 1, 3, col); } else px(c, hx, hy, 1, 1, col);
      }
      if (fx === 'regen') ring3(c, x, y - 2, 12 * Math.min(1, k * 2), 4 * Math.min(1, k * 2), '#8fe08a', 16);
      break;
    }
    case 'sleep': {
      for (let i = 0; i < 3; i++) { const t = k - i * 0.2; if (t < 0) continue; glyphZ(c, x + 4 + i * 4 + Math.sin(t * 6) * 2, head - 2 - t * 12, i === 0 ? '#ffffff' : '#c9d8ff'); }
      break;
    }
    case 'poison': case 'spore': {
      const col = fx === 'poison' ? '#b36ad9' : '#9ccf6a';
      for (let i = 0; i < 14; i++) { const life = (k * 1.4 - r(seed, i) * 0.4); if (life < 0 || life > 1) continue; const s = 1 + Math.round(r(seed, i + 3) * 2); bubble(c, x + (r(seed, i + 11) - 0.5) * 18, y - 3 - life * top, s, col); }
      break;
    }
    case 'steal': {
      for (let i = 0; i < 10; i++) { const a = i / 10 * Math.PI * 2 + k * 4, d = 4 + k * 12; px(c, x + Math.cos(a) * d, mid + Math.sin(a) * d * 0.6, 2, 2, i % 2 ? '#ffe9a8' : '#ffffff'); }
      if (k > 0.3 && k < 0.8) { px(c, x - 1, mid - 4, 3, 3, '#f9c776'); px(c, x, mid - 3, 1, 1, '#fff6d8'); }   // the coin
      break;
    }
    case 'haste': {
      for (let i = 0; i < 6; i++) { const yy = head + 4 + i * (top - 6) / 5, len = 4 + r(seed, i) * 6, off = ((k * 40 + i * 7) % 20); px(c, x + 8 + off, yy, len, 1, i % 2 ? '#ffe9a8' : '#ffffff'); }
      break;
    }
    case 'smoke': case 'drain': {
      const col = fx === 'smoke' ? '#9aa0a8' : '#8a5ab0';
      for (let i = 0; i < 10; i++) { const a = r(seed, i) * Math.PI * 2, d = k * 16 * (0.5 + r(seed, i + 5)), s = Math.round(2 + k * 4 * r(seed, i + 9)); c.globalAlpha = fade * 0.8; px(c, x + Math.cos(a) * d - s / 2, mid + Math.sin(a) * d * 0.6 - s / 2, s, s, col); }
      break;
    }
    case 'kick': case 'bash': {
      const s = Math.round(4 + k * 8);
      for (let i = 0; i < 8; i++) { const a = i / 8 * Math.PI * 2, l = i % 2 ? 2 : 4; for (let j = 0; j < l; j++) px(c, x + Math.cos(a) * (s + j), mid + Math.sin(a) * (s + j) * 0.8, 2, 2, i % 2 ? '#ffffff' : '#ffe9a8'); }
      break;
    }
    case 'burst': {
      const cols = ['#ffe97a', '#ff8a6a', '#8fd0ff', '#ffffff'];
      for (let i = 0; i < 36; i++) { const a = i / 36 * Math.PI * 2, d = 4 + k * 46 * (0.6 + r(seed, i) * 0.4); px(c, x + Math.cos(a) * d, mid + Math.sin(a) * d * 0.55, 2, 2, cols[i % 4]); }
      ring3(c, x, mid, k * 50, k * 26, '#ffffff', 40);
      break;
    }
  }
  c.restore();
}

/** A whole-screen flash for the big moments (thunder, bursts): 0..1 strength at k. */
export function flashOf(fx: SpellFx, k: number): number {
  if (fx === 'thunder') return k > 0.2 && k < 0.32 ? 0.6 : 0;
  if (fx === 'burst') return k < 0.15 ? 0.7 * (1 - k / 0.15) : 0;
  return 0;
}

/** Little marks over someone's head for their statuses (some move). */
export function drawStatusMarks(c: C, st: [StatusId, number][] | undefined, x: number, head: number, now: number): void {
  if (!st?.length) return;
  const shown = st.filter(([id]) => id !== 'sleep' && id !== 'stun');
  shown.forEach(([id], i) => icon(c, id, Math.round(x - (shown.length * 6) / 2 + i * 6 + 1), Math.round(head - 7)));
  if (st.some(([id]) => id === 'sleep')) { const t = (now % 1400) / 1400; glyphZ(c, x + 6 + Math.sin(t * 6) * 1.5, head - 2 - t * 8, '#ffffff'); }
  if (st.some(([id]) => id === 'stun')) for (let i = 0; i < 2; i++) { const a = now / 180 + i * Math.PI; px(c, x + Math.cos(a) * 7, head - 3 + Math.sin(a) * 2, 2, 2, '#ffe97a'); }
  if (st.some(([id]) => id === 'poison') && Math.floor(now / 500) % 3 === 0) bubble(c, x - 6, head + 4 - ((now % 500) / 500) * 6, 1, '#b36ad9');
}

function icon(c: C, id: StatusId, x: number, y: number): void {
  const ink = '#2f2a26';
  px(c, x - 1, y - 1, 6, 6, ink);
  switch (id) {
    case 'poison': px(c, x, y, 4, 4, '#b36ad9'); px(c, x + 1, y + 1, 1, 1, '#f3d8ff'); break;
    case 'slow': px(c, x, y, 4, 4, '#5e7fb3'); px(c, x + 1, y + 1, 2, 2, '#bcd2f5'); break;
    case 'haste': px(c, x, y, 4, 4, '#f9c776'); px(c, x + 1, y, 2, 1, '#fff6d8'); px(c, x, y + 1, 4, 1, '#fff6d8'); break;
    case 'protect': px(c, x, y, 4, 4, '#8fc7ff'); px(c, x + 1, y + 1, 2, 2, '#ffffff'); break;
    case 'regen': px(c, x, y, 4, 4, '#6fbf5a'); px(c, x + 1, y, 2, 4, '#e9ffe0'); px(c, x, y + 1, 4, 2, '#e9ffe0'); break;
    case 'provoke': px(c, x, y, 4, 4, '#ec5448'); px(c, x, y, 1, 1, '#ffd8d0'); px(c, x + 3, y + 3, 1, 1, '#ffd8d0'); break;
    case 'sentinel': px(c, x, y, 4, 4, '#a3def4'); px(c, x + 1, y + 1, 2, 3, '#4e83ad'); break;
    case 'blind': px(c, x, y, 4, 4, '#5a5f66'); px(c, x, y + 1, 4, 1, '#c8ccd2'); break;
    default: px(c, x, y, 4, 4, '#ffffff');
  }
}
function glyphZ(c: C, x: number, y: number, col: string): void { px(c, x, y, 3, 1, col); px(c, x + 1, y + 1, 1, 1, col); px(c, x, y + 2, 3, 1, col); }
function bubble(c: C, x: number, y: number, s: number, col: string): void { px(c, x, y, s + 1, s + 1, col); px(c, x, y, 1, 1, '#ffffff'); }
function ring3(c: C, x: number, y: number, rx: number, ry: number, col: string, n: number): void { for (let i = 0; i < n; i++) { const a = i / n * Math.PI * 2; px(c, x + Math.cos(a) * rx, y + Math.sin(a) * ry, 1, 1, col); } }
function line(c: C, x0: number, y0: number, x1: number, y1: number, col: string): void {
  const n = Math.max(1, Math.ceil(Math.hypot(x1 - x0, y1 - y0)));
  for (let i = 0; i <= n; i++) px(c, x0 + (x1 - x0) * i / n, y0 + (y1 - y0) * i / n, 1, 1, col);
}
